import {createSelector} from 'reselect';
import Page from '../../enums/Page';
import Sections from '../../enums/Sections';
import getContactNumberRulesByCountry from '../form/field-validation/getContactNumberRulesByCountry';
import stringContainsOnlyChineseCharacters
  from '../form/field-validation/ImportTax/stringContainsOnlyChineseCharacters';
import maxLatinChars from '../form/field-validation/maxLatinChars';
import getPage from '../page/getPage';
import getOrderDeliveryAddress from './DeliveryAddressSelectors/getOrderDeliveryAddress';
import isEditedAddressSelected from './DeliveryAddressSelectors/isEditedAddressSelected';
import {getIsContactNumberOptionalExperiment} from './getIsContactNumberOptionalExperiment';
import getAppliedGiftcards from './GiftCardSelectors/getAppliedGiftcards';
import {getSelectedPaymentOptionIndex} from './PaymentOptionSelectors/getSelectedPaymentOptionIndex';
import {getPudo, getSelectedPudoAddress, isSelectedNewPudoAddress} from './PudoSelectors';

export const getDeliveryType = state => state.delivery.deliveryType;
export const getDelivery = state => state.delivery;
export const isDeliveryTypeValid = state => state.delivery.isValid;
const getPaymentOptions = state => state.payment.options;
const getUsedCreditValue = state => state.orderSummary.credit?.used?.value;
export const getRemainingCreditValue = state => state.orderSummary.credit?.remaining?.value;
export const getOrderTotal = state => state.orderSummary.orderTotal.value;
export const getStart = state => state.start;
export const getCustomerEmail = state => state.start?.customer?.email;
export const getSections = state => state.start.sections;
export const getSettings = state => state.start?.settings;
export const getApplepayConfiguration = state => state.start.applePayConfiguration;
export const getCurrency = state => state.start.orderTotal.currencyCode;
export const getOrderSummary = state => state.orderSummary;
export const getSite = state => state.site;
export const getSiteCode = state => state.start.siteDataV2.siteCode;
export const getSiteId = state => state.start.siteDataV2.siteId;
export const getLocaleFromSiteDataV2 = state => state.start.siteDataV2.locale;

export const isExistingUser = state => (state.address.deliveryAddressList.savedAddressIds.length > 0);
export const getContactNumber = state => state.address.deliveryAddressList.contactNumber;
export const getEmailAddress = state => state.email.emailAddress;
export const isOptedIntoMarketing = state => state.email.optedIntoMarketing;
export const hasDigitalConsent = state => state.acknowledgedMessage.hasDigitalConsent;
export const hasDeliveryAddress = state => !!state.start.deliveryAddress;
export const isConsentGiven = state => state.acknowledgedMessage.consentGiven;

export const getPasswordRules = state => state.start.validationRules.password;
export const getLoyaltyPoints = state => state.start?.order?.loyaltyPoints?.totalPointsEarned || 0;

export const getSelectedPaymentOption = createSelector(
  [getSelectedPaymentOptionIndex, getPaymentOptions, getAppliedGiftcards],
  (optionIndex, paymentOptions, appliedGiftcards) => {
    if (optionIndex === -1 || optionIndex === null) {
      if (appliedGiftcards.length) {
        if (appliedGiftcards.some(giftcard => giftcard.paymentMethod?.toUpperCase() === 'DIGGECARD')) {
          return {
            type: 'diggecard',
            name: 'diggecard',
          };
        }
        
        return {
          type: 'gift_card',
          name: 'gift_card',
        };
      }
      return {
        type: 'credit',
        name: 'credit',
      };
    }
    return paymentOptions[optionIndex];
  },
);

export const isPudoAvailable = createSelector(
  [getPudo, getDelivery],
  (pudo, delivery) => !!pudo.currentLocation.country && delivery.isPudoAvailable,
);

export const isCreditUsed = createSelector(
  [getUsedCreditValue],
  usedCredit => !!usedCredit && usedCredit !== '0',
);

export const getSelectedPaymentOptionType = createSelector(
  [getSelectedPaymentOption],
  selectedPaymentOption => selectedPaymentOption?.type,
);

export const getPaymentOptionsByType = type => createSelector(
  [getPaymentOptions],
  paymentOptions => paymentOptions.filter(option => option.type === type),
);

export const getSelectedAddressID = state => state.address.deliveryAddressList?.addressId;

export const getSelectedAddressCategory = state => state.address.deliveryAddressList?.selectedAddressCategory;

const getAddresses = state => state.address.deliveryAddressList?.addresses;

export const getNewAddress = state => getAddresses(state)?.[''];

export const getDeliveryCountryUsedForDeliveryOptions = state => state.deliveryOption.deliveryCountryForDeliveryOptions;

export const getSelectedResidentialAddress = createSelector(
  [getSelectedAddressID, getAddresses],
  (selectedAddressID, addresses) => addresses?.[selectedAddressID],
);

export const getSelectedContactNumber = createSelector(
  [getSelectedResidentialAddress],
  selectedAddress => selectedAddress?.contactNumber || '',
);

export const getSelectedDeliveryCountry = createSelector(
  [getSelectedResidentialAddress, getOrderDeliveryAddress],
  (selectedAddress, orderDeliveryAddress) => selectedAddress?.country
    || orderDeliveryAddress?.countryCode,
);

export const getSelectedCollectionCountry = createSelector(
  [getSelectedPudoAddress, getOrderDeliveryAddress],
  (selectedAddress, orderDeliveryAddress) => selectedAddress?.country
    || orderDeliveryAddress?.countryCode,
);

export const getContactNumberRules = createSelector(
  [getDeliveryType, getSelectedDeliveryCountry, getSelectedCollectionCountry],
  (deliveryType, selectedDeliveryCountry, selectedCollectionCountry) => {
    const country = deliveryType === 'pudo' ? selectedCollectionCountry : selectedDeliveryCountry;
    return getContactNumberRulesByCountry(country);
  },
);

export const selectIsContactNumberRequired = createSelector(
  [getSite, getPaymentOptions, getContactNumberRules, getDeliveryType, getIsContactNumberOptionalExperiment, getOrderTotal, getSelectedDeliveryCountry, getSettings],
  (site, paymentOptions, contactNumberRules, deliveryType, isExperiment, orderTotal, deliveryCountry, settings) => {
    const paymentOptionsRequiringContactNumber = isExperiment ? ['ARVATO', 'ATOME', 'PAY_ON_DELIVERY'] : ['LAYBUY', 'ARVATO', 'ATOME', 'PAY_ON_DELIVERY'];
    if (settings?.always_require_contact_number_when_frasers_plus_on_page) {
      paymentOptionsRequiringContactNumber.push('FRASERS_PLUS');
    }
    const isDeliveryTypePudo = deliveryType === 'pudo';
    
    const doesAnyPaymentOptionRequireNumber = paymentOptions
      .some(paymentType => paymentOptionsRequiringContactNumber.includes(paymentType.type));
    
    if (isExperiment && deliveryCountry?.toLowerCase() === 'gb') {
      return doesAnyPaymentOptionRequireNumber || isDeliveryTypePudo || orderTotal > 1000;
    }
    
    return site.isContactNumberThresholdExceeded || doesAnyPaymentOptionRequireNumber || contactNumberRules.required
      || isDeliveryTypePudo;
  },
);

export const getSelectedAddressInAPIFormat = createSelector(
  [getSelectedResidentialAddress, getContactNumber],
  (selectedAddress, contactNumber) => (selectedAddress && {
    addressId: selectedAddress.addressId,
    addressee: selectedAddress.addressee,
    companyName: selectedAddress.companyName,
    houseNameNumber: selectedAddress.nameNumber,
    streetName: selectedAddress.streetName,
    addressLine2: selectedAddress.addressLine2,
    town: selectedAddress.townCity,
    county: selectedAddress.county,
    postCode: selectedAddress.postCode,
    countryCode: selectedAddress.country,
    residentId: selectedAddress.residentId,
    contactNumber,
  }),
);

export const isSelectedAddressValidForCBEC = createSelector(
  [getSelectedResidentialAddress],
  (selectedAddress) => {
    const addressFieldsToCheck = [
      'county',
      'townCity',
      'streetName',
      'addressLine2',
    ];
    
    const chineseCharOnlyFields = [
      'addressee',
      'companyName',
    ];
    
    const requiredFields = [
      'addressee',
      'county',
      'townCity',
      'streetName',
      'addressLine2',
    ];
    
    let hasValidChars = true;
    
    addressFieldsToCheck.forEach((field) => {
      if (!stringContainsOnlyChineseCharacters(selectedAddress[field])) {
        const doesExceedMaxLatinChars = maxLatinChars(field, 5);
        
        if (doesExceedMaxLatinChars(selectedAddress[field])) {
          hasValidChars = false;
        }
      }
    });
    
    const hasChineseCharsOnly = chineseCharOnlyFields.reduce(
      (chineseOnly, fieldName) => chineseOnly && stringContainsOnlyChineseCharacters(selectedAddress[fieldName]), true,
    );
    
    const hasRequiredFields = requiredFields.reduce(
      (required, fieldName) => required
        && selectedAddress[fieldName] !== undefined && selectedAddress[fieldName]?.trim()?.length > 0, true,
    );
    
    return hasValidChars && hasRequiredFields && hasChineseCharsOnly;
  },
);

export const isSelectedAddressNewAddress = createSelector(
  [getSelectedAddressID],
  selectedAddressID => selectedAddressID === '',
);

export const isCV2Required = createSelector(
  [getOrderSummary, isSelectedAddressNewAddress, getDeliveryType, isSelectedNewPudoAddress, isEditedAddressSelected],
  (orderSummary, isNewAddress, deliveryType, isNewPudoAddress, isEditedAddress) => !!(orderSummary.cv2Required)
    || (isNewAddress && deliveryType !== 'pudo')
    || (deliveryType === 'pudo' && isNewPudoAddress) || isEditedAddress,
);

const getSiteData = state => state.site;

export const isOrderProcessing = createSelector(
  [getSiteData],
  siteData => siteData.orderIsProcessing,
);

export const isGuest = createSelector(
  [getStart],
  start => start.guestCheckout,
);

export const getSiteDataV2 = createSelector(
  [getStart],
  start => start.siteDataV2,
);

export const hasEarntLoyaltyPoints = createSelector(
  [getStart],
  start => start.order.loyaltyPoints && start.order.loyaltyPoints.totalPointsEarned > 0,
);

export const getOrderNumber = createSelector(
  [getStart],
  start => start.orderNumber,
);

export const isAccountHolder = createSelector(
  [getStart],
  start => start.accountHolder,
);

export const isNewGuestUpdatedPage = createSelector(
  [getSiteDataV2, isGuest, isAccountHolder, getSettings],
  (siteDataV2, guest, accountHolder, settings) => !!(guest && !accountHolder && settings.is_new_guest_updated_page),
);

export const getGuestCheckoutAccountBenefits = createSelector(
  [getSettings],
  settings => settings?.guest_checkout_account_benefits,
);

export const isZeroValueOrder = createSelector(
  [getOrderSummary],
  orderSummary => orderSummary.orderTotal.value === 0 || !!orderSummary.zeroOrderTotal, // TODO refactor after new OS
);

export const shouldCaptureBillingAddress = createSelector(
  [getDeliveryType, getSelectedResidentialAddress, hasDeliveryAddress, getSections, getPage],
  (deliveryType, selectedResidentialAddress, deliveryAddressExists, sections, page) => {
    const hasSections = page === Page.CHECKOUT;
    const hasAddressSection = hasSections && sections.includes(Sections.ADDRESS);
    const hasValidResidentialAddressSelected = deliveryType !== 'pudo' && selectedResidentialAddress && hasAddressSection;
    return !(hasValidResidentialAddressSelected || deliveryAddressExists);
  },
);
